import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Truck, Container, AlertTriangle, CheckCircle, Settings, Zap, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useApi } from '../hooks/useApi';

/**
 * InspectionVehicleSelector
 * ========================
 * Page d'accueil de l'inspection :
 * - Affiche le camion + remorques selectionnes dans "Je conduis aujourd'hui"
 * - Une carte par vehicule avec badge "A inspecter" / "Inspectee"
 * - Click sur une carte -> appel onSelect(vehicle) qui charge l'inspection adaptee
 * - Bouton "Tout inspecter d'un coup" (mode wizard)
 * - Si aucun vehicule -> CTA pour configurer
 */
export const InspectionVehicleSelector = ({ onSelect, onStartWizard }) => {
  const { get } = useApi();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAssignment();
  }, []);

  const fetchAssignment = async () => {
    try {
      const r = await get('/driver/assignment/current');
      if (r.success) setAssignment(r.data);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 space-y-3">
        <div className="h-32 bg-slate-800/50 rounded-2xl animate-pulse" />
        <div className="h-32 bg-slate-800/50 rounded-2xl animate-pulse" />
      </div>
    );
  }

  const truck = assignment?.truck;
  const trailers = assignment?.trailers || [];
  const allVehicles = [
    ...(truck ? [{ ...truck, _vehicle_type: 'truck' }] : []),
    ...trailers.map((t) => ({ ...t, _vehicle_type: 'trailer' })),
  ];

  const toInspectCount = allVehicles.filter((v) => v.needs_inspection).length;
  const totalCount = allVehicles.length;

  // Aucun vehicule configure
  if (totalCount === 0) {
    return (
      <div className="p-4">
        <div className="bg-amber-500/10 border-2 border-amber-500/40 rounded-2xl p-6 text-center">
          <AlertTriangle size={48} className="mx-auto text-amber-400 mb-3" />
          <h2 className="text-xl font-bold text-white mb-2">Aucun véhicule sélectionné</h2>
          <p className="text-slate-300 text-sm mb-5">
            Avant d'inspecter, configurez votre <strong>camion</strong> et vos{' '}
            <strong>remorques</strong> dans « Je conduis aujourd'hui ».
          </p>
          <Button
            onClick={() => navigate('/dashboard')}
            className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold h-14 text-base"
            data-testid="goto-dashboard-config"
          >
            <Settings size={18} className="mr-2" />
            Configurer mes véhicules
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Header avec progression */}
      <div className="text-center pb-2">
        <h2 className="text-xl font-bold text-white mb-1">Que voulez-vous inspecter ?</h2>
        <p className="text-sm text-slate-400">
          {toInspectCount > 0 ? (
            <span className="text-amber-400">
              {toInspectCount} véhicule{toInspectCount > 1 ? 's' : ''} à inspecter
            </span>
          ) : (
            <span className="text-green-400">Tous les véhicules sont inspectés ✓</span>
          )}
          {' · '}
          {totalCount} véhicule{totalCount > 1 ? 's' : ''} aujourd'hui
        </p>
      </div>

      {/* Wizard CTA - cache temporairement (logique a venir) */}
      {false && toInspectCount >= 2 && (
        <Button
          onClick={() => onStartWizard?.(allVehicles.filter((v) => v.needs_inspection))}
          className="w-full h-14 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold text-base"
          data-testid="start-wizard-btn"
        >
          <Zap size={20} className="mr-2" />
          Tout inspecter d'un coup ({toInspectCount} véhicules)
        </Button>
      )}

      {/* Cartes vehicules */}
      <div className="space-y-3">
        {truck && (
          <VehicleCard
            vehicle={truck}
            type="truck"
            label="CAMION"
            icon={Truck}
            onClick={() => onSelect({ ...truck, _vehicle_type: 'truck' })}
          />
        )}

        {trailers.map((t, idx) => (
          <VehicleCard
            key={t.id}
            vehicle={t}
            type="trailer"
            label={trailers.length === 2 ? `REMORQUE ${idx + 1}` : 'REMORQUE'}
            icon={Container}
            onClick={() => onSelect({ ...t, _vehicle_type: 'trailer' })}
          />
        ))}
      </div>

      {/* Footer avec lien vers banner */}
      <div className="pt-4 border-t border-slate-800">
        <button
          onClick={() => navigate('/dashboard')}
          className="w-full text-center text-sm text-slate-400 hover:text-amber-400 transition-colors py-2"
          data-testid="change-vehicles-btn"
        >
          ← Changer de véhicule(s) sur le tableau de bord
        </button>
      </div>
    </div>
  );
};

const VehicleCard = ({ vehicle, type, label, icon: Icon, onClick }) => {
  const needsInsp = vehicle.needs_inspection;
  const lastStatus = vehicle.last_inspection_status;

  return (
    <button
      onClick={onClick}
      className={`w-full text-left rounded-2xl p-4 border-2 transition-all active:scale-[0.99] ${
        needsInsp
          ? 'bg-amber-500/10 border-amber-500/50 hover:border-amber-500 hover:bg-amber-500/15'
          : 'bg-slate-800/40 border-slate-700 hover:border-slate-600'
      }`}
      data-testid={`inspect-vehicle-${vehicle.id}`}
    >
      <div className="flex items-start gap-3">
        {/* Icone vehicule */}
        <div className={`rounded-xl p-3 ${
          needsInsp ? 'bg-amber-500/20' : 'bg-slate-700/40'
        }`}>
          <Icon size={28} className={needsInsp ? 'text-amber-400' : 'text-slate-400'} />
        </div>

        {/* Infos vehicule */}
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-bold tracking-wider mb-1 ${
            needsInsp ? 'text-amber-400' : 'text-slate-500'
          }`}>
            {label}
          </p>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-lg font-bold text-white">
              {vehicle.unit_number ? `#${vehicle.unit_number}` : vehicle.plate_number || 'Véhicule'}
            </span>
            {vehicle.is_manual && (
              <span className="text-[10px] bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded-full font-semibold">
                LOUÉ
              </span>
            )}
          </div>
          <div className="text-sm text-slate-300">
            {[vehicle.make, vehicle.model].filter(Boolean).join(' ')}
            {vehicle.plate_number && vehicle.unit_number && (
              <span className="text-slate-500 ml-2">{vehicle.plate_number}</span>
            )}
          </div>
        </div>

        {/* Badge statut + chevron */}
        <div className="flex flex-col items-end gap-2">
          {needsInsp ? (
            <span className="inline-flex items-center gap-1 text-xs font-bold bg-amber-500 text-black px-3 py-1 rounded-full">
              <AlertTriangle size={12} />
              À INSPECTER
            </span>
          ) : (
            <span className={`inline-flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full ${
              lastStatus === 'failed'
                ? 'bg-red-500/20 text-red-300'
                : 'bg-green-500/20 text-green-300'
            }`}>
              <CheckCircle size={12} />
              {lastStatus === 'failed' ? 'AVEC DÉFAUTS' : 'INSPECTÉ'}
            </span>
          )}
          <ArrowRight size={20} className="text-slate-500" />
        </div>
      </div>
    </button>
  );
};
